import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";

/** Config pages live at `/w/$workspaceId/$pageId` (not under `/ottopilot/test`). */
export function configPageHref(workspaceId: Uuid | string, pageId: Uuid | string): string {
    return `/w/${workspaceId}/${pageId}`;
}

/** TanStack `to` path for config pages. */
export const CONFIG_PAGE_ROUTE_TO = "/w/$workspaceId/$pageKey" as const;

/** Existing named workspace routes that must not be treated as config page ids. */
export const RESERVED_WORKSPACE_PATH_SEGMENTS = new Set([
    "ottopilot",
    "ottokart",
    "ottosync",
    "test",
    "account-settings",
    "billing",
    "collections",
    "dashboard",
    "bluestores",
    "favourite-reports",
    "koios",
    "notifications",
    "organizational-unit-templates",
    "organizational-units",
    "product-catalogue-templates",
    "product-inventories",
    "products",
    "profile",
    "reports",
    "store-configuration",
    "user-invitation",
    "user-templates",
    "users",
]);

export function isReservedWorkspacePathSegment(segment: string): boolean {
    return segment.startsWith("_") || RESERVED_WORKSPACE_PATH_SEGMENTS.has(segment);
}

/**
 * Match `/w/{workspaceId}/{pageKey}` where pageKey is a config page id.
 * Accepts a pathname or a full href, including nested paths such as
 * `/w/{workspaceId}/{pageKey}/folder/file.jpg` used by the media library.
 */
export function extractConfigPageKey(pathname: string): string | null {
    const pathOnly = pathname.split("?")[0]?.split("#")[0] ?? pathname;
    const match = pathOnly.match(/^\/w\/[^/]+\/([^/]+)(?:\/|$)/);
    if (match === null || match[1] === undefined) {
        return null;
    }
    const pageKey = match[1];
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(pageKey) !== true) {
        return null;
    }
    return pageKey;
}

export function configPageUrlPattern(pageId: string): RegExp {
    const escapedPageId = pageId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return new RegExp(String.raw`^\/w\/[a-zA-Z0-9_-]+\/${escapedPageId}(\/.*)?(\?.*)?$`);
}
